<template>
    <v-row>
      <v-col cols="12">
        <h1 v-heading>Fragen und Antworten</h1>
      </v-col>
      
      <v-col
        cols="12"
        md="12"
      >
        <v-expansion-panels>
          <v-expansion-panel
            v-for="(item, i) in faqItems"
            :key="i"
          >
            <v-expansion-panel-title>
              <v-row no-gutters>
                <v-col
                  cols="12"
                  class="text-h6"
                >
                  {{ item.question }}
                </v-col>
              </v-row>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div v-html="item.answer"></div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import headerGenerator from '~/utils/headerGenerator';

definePageMeta({
  layout: 'default-public'
});

useHead({
  ...headerGenerator(null, null, 'Fragen und Antworten')
})

const faqItems = ref([
  {
    question: 'Gibt es langfristige Vertragsbindungen?',
    answer: `
      <p>Nein, TerminTiger bietet Ihnen maximale Flexibilität ohne langfristige Bindungen:</p>
      <ul>
        <li>Monatliche Abrechnung: Sie zahlen nur für die tatsächliche Nutzungsdauer</li>
        <li>Flexible Kündigung: Sie können den Service zum Ende jedes Monats beenden</li>
        <li>Kostenlose Testphase: Testen Sie TerminTiger 30 Tage lang unverbindlich</li>
        <li>Anpassbare Pakete: Wechseln Sie jederzeit zwischen verschiedenen Leistungspaketen</li>
      </ul>
      <p>Wir sind von der Qualität unseres Services überzeugt und setzen auf Kundenzufriedenheit statt auf langfristige Vertragsbindungen.</p>
    `
  },
  {
    question: 'Wie kann TerminTiger zur Umsatzsteigerung beitragen?',
    answer: `
      <p>TerminTiger bietet verschiedene Möglichkeiten zur Umsatzsteigerung:</p>
      <ul>
        <li>Optimierte Auslastung: Füllen Sie freie Zeitfenster durch intelligente Terminvorschläge</li>
        <li>Attraktive Angebote: Erstellen Sie Paketangebote, um den Durchschnittsumsatz pro Kunde zu erhöhen</li>
        <li>Kundenbindungsprogramme: Implementieren Sie Treueprogramme zur Förderung von Stammkunden</li>
        <li>Proaktive Terminerinnerungen: Erinnern Sie Kunden automatisch an fällige Folgetermine</li>
        <li>Gutscheinverkäufe: Ermöglichen Sie den Verkauf von Geschenkgutscheinen</li>
        <li>Cross-Selling: Empfehlen Sie passende Zusatzleistungen zu gebuchten Terminen</li>
        <li>Erhöhte Reichweite durch SEO Optimierung und SEA</li>
      </ul>
      <p>Unser Expertenteam unterstützt Sie gerne bei der Implementierung dieser umsatzsteigernden Strategien.</p>
    `
  },
  {
    question: 'Welche Vorteile bietet die Nutzung von TerminTiger?',
    answer: `
      <p>TerminTiger bietet zahlreiche Vorteile für Ihr Unternehmen:</p>
      <ul>
        <li>Zeitersparnis: Automatisierte Terminbuchungen und reduzierter Verwaltungsaufwand</li>
        <li>24/7 Erreichbarkeit: Ermöglichen Sie Ihren Kunden Buchungen rund um die Uhr</li>
        <li>Reduzierung von Terminausfällen: Automatische Erinnerungen fördern die Pünktlichkeit Ihrer Kunden</li>
        <li>Effiziente Planung: Behalten Sie den Überblick über alle Termine und Mitarbeiterkapazitäten</li>
        <li>Erhöhte Kundenzufriedenheit: Bieten Sie einen modernen und benutzerfreundlichen Service</li>
        <li>Kundenakquise: Nutzen Sie unsere Marketing-Tools zur Gewinnung neuer Kunden</li>
      </ul>
      <p>Mit TerminTiger können Sie sich auf das Wesentliche konzentrieren: Ihre Kunden und Ihr Kerngeschäft.</p>
    `
  },
  {
    question: 'Welche Kosten fallen für den Service an?',
    answer: `
      <p>Wir bieten verschiedene Preismodelle, abgestimmt auf Ihre individuellen Bedürfnisse:</p>
      <ul>
        <li>Basic-Paket: 19€ monatlich, ideal für Selbstständige</li>
        <li>Pro-Paket: 29€ monatlich, geeignet für wachsende Geschäfte</li>
        <li>Enterprise-Paket: 999€ monatlich, maßgeschneidert für große Unternehmen</li>
      </ul>
      <p>Sie haben die Möglichkeit, alle Funktionen 30 Tage lang kostenlos zu testen. Detaillierte Informationen finden Sie auf unserer Preisübersicht.</p>
      <p>Bei Online Bezahlung fallen üblichen 2.5% für die Abwicklung der Kartenzahlung an.</p>
    `
  },
  {
    question: 'Wann erfolgt die Auszahlung an den Betreiber?',
    answer: `
      <p>Die Auszahlung erfolgt stets am 15. des Folgemonats. Sie umfasst alle Zahlungen für Termine, die in den letzten zwei Wochen des Vormonats beglichen wurden.</p>
    `
  },
  {
    question: 'Wie sicher sind die Zahlungen?',
    answer: `
      <p>Die Zahlungen erfolgen über sicheren Zahlungsdienstleister - Stripe. Ihre Daten sind durch modernste Verschlüsselungstechnologien geschützt.</p>
      <p>Wir garantieren eine sichere und zuverlässige Abwicklung</p>
    `
  },
  {
    question: 'Welche Zahlungsmethoden werden akzeptiert?',
    answer: `
      <p>Wir akzeptieren alle gängigen Zahlungsmethoden:</p>
      <ul>
        <li>Kreditkarte: Visa, Mastercard, American Express</li>
        <li>SEPA-Lastschrift</li>
        <li>Apple Pay</li>
        <li>Google Pay</li>
      </ul>
      <p>Die Zahlungsmethoden können je nach Land variieren.</p>
    `
  },
  {
    question: 'Wie gehe ich mit Kunden um, die nicht erscheinen?',
    answer: `
      <p>Zunächst sollten Sie den Kontakt zum Kunden suchen. Möglicherweise lässt sich ein alternativer Termin vereinbaren.</p>
      <p>Bei wiederholtem Nichterscheinen können Sie den Kunden von Ihrer Kundenliste entfernen und auf die Blacklicst setzen.</p>
      <p>Sie haben auch die Option, eine Vorkasse vom Kunden zu verlangen. Die Vorkassenleistung muss NICHT erstattet werden, das entscheiden Sie selbst.</p>
    `
  },








  // {
  //   question: 'Wie bucht ein Kunde einen Termin?',
  //   answer: `
  //     <p>Kunden können bequem online buchen. Sie wählen ihre gewünschte Dienstleistung und den passenden Zeitpunkt aus.</p>
  //     <p>Je nach Einstellung wird der Termin entweder sofort bestätigt oder erfordert eine Genehmigung durch den Betreiber. Dies verhindert Doppelbuchungen.</p>
  //     <p>In einigen Fällen ist eine Vorauszahlung erforderlich, um No-Shows zu minimieren.</p>
  //     <p>Kunden haben die Möglichkeit, ihre Termine bei Bedarf zu ändern oder zu stornieren.</p>
  //     <p>Wir senden automatische E-Mails oder SMS, um Kunden stets auf dem Laufenden zu halten.</p>
  //   `
  // },
  // {
  //   question: 'Wie verwaltet der Betreiber die Termine?',
  //   answer: `
  //     <p>Der Betreiber erhält umgehend eine E-Mail-Benachrichtigung bei jeder neuen Terminanfrage.</p>
  //     <p>Abhängig von den gewählten Einstellungen werden Termine entweder automatisch bestätigt oder erfordern eine manuelle Freigabe durch den Betreiber.</p>
  //     <p>In der Terminübersicht kann der Betreiber den Zahlungsstatus einsehen und Termine mit einem Klick bestätigen oder ablehnen.</p>
  //     <p>Neben E-Mails können Benachrichtigungen auch über SMS oder WhatsApp eingerichtet werden.</p>
  //     <p>Für eine optimale Verwaltung empfehlen wir die Nutzung unserer benutzerfreundlichen Terminmanagement-App.</p>
  //   `
  // },
  
  // {
  //   question: 'Wie kann ich meine Mitarbeiter einladen?',
  //   answer: `
  //     <p>Das Einladen von Mitarbeitern ist unkompliziert. Geben Sie einfach die E-Mail-Adresse des Mitarbeiters ein und definieren Sie die entsprechenden Zugriffsrechte.</p>
  //   `
  // },
  
  {
    question: 'Wie finden Kunden diese Buchungsplattform?',
    answer: `
      <p>Kunden entdecken uns über verschiedene Kanäle wie Google, Facebook, Instagram und andere Online-Plattformen. Ein wichtiges Marketinginstrument ist auch der QR-Code, den Sie auf Ihrer Website, an Ihrer Tür oder auf Tischen platzieren können.</p>
    <p>Wir unterstützen Sie aktiv bei Ihrem Marketing:</p>
    <ul>
      <li>Wir schalten gezielte Google Ads und Facebook-Anzeigen, um Ihre Reichweite zu erhöhen.</li>
      <li>Wir optimieren Ihre Online-Präsenz für bessere Suchmaschinen-Rankings.</li>
      <li>Wir betreiben regelmäßiges Social-Media-Marketing, um neue Kunden für Sie zu gewinnen.</li>
      <li>Sie erhalten von uns wertvolle Tipps zur Eigenvermarktung.</li>
    </ul>
    <p>So steigern wir Ihre Kundengewinnung, ohne dass Sie selbst viel Zeit investieren müssen.</p>
    `
  },
  
  {
    question: 'Wie beginne ich mit der Nutzung?',
    answer: `
      <p>Der Einstieg ist einfach und schnell:</p>
      <ol>
        <li>Erstellen Sie Ihr Konto</li>
        <li>Nutzen Sie unseren interaktiven Onboarding-Assistenten</li>
        <li>Definieren Sie Ihr Dienstleistungsangebot und die Preisstruktur</li>
        <li>Laden Sie bei Bedarf Ihre Mitarbeiter ein</li>
        <li>Integrieren Sie den Service in Ihre bestehende Website</li>
        <li>Profitieren Sie von unseren umfangreichen Schulungsvideos</li>
        <li>Unser Support-Team steht Ihnen bei Fragen jederzeit zur Verfügung</li>
      </ol>
      <p>Die gesamte Einrichtung nimmt in der Regel nur wenige Stunden bis maximal einen Tag in Anspruch.</p>
    `
  },
  // {
  //   question: 'Welche Anpassungsmöglichkeiten bietet TerminTiger?',
  //   answer: `
  //     <p>TerminTiger lässt sich umfassend an Ihre individuellen Bedürfnisse anpassen:</p>
  //     <ul>
  //       <li>Corporate Design: Passen Sie Farben und Layout an Ihre Unternehmensidentität an</li>
  //       <li>Dienstleistungskatalog: Definieren Sie Ihr Angebot inklusive Dauer und Preisgestaltung</li>
  //       <li>Mitarbeiterverwaltung: Legen Sie Arbeitszeiten und Kompetenzen Ihrer Mitarbeiter fest</li>
  //       <li>Buchungsrichtlinien: Bestimmen Sie Fristen für Buchungen und Stornierungen</li>
  //       <li>Kommunikation: Personalisieren Sie E-Mails und SMS nach Ihrem Kommunikationsstil</li>
  //       <li>Integrationen: Verknüpfen Sie TerminTiger nahtlos mit Ihren bestehenden Systemen</li>
  //     </ul>
  //     <p>Unser Support-Team steht Ihnen zur Seite, um TerminTiger optimal auf Ihre Geschäftsprozesse abzustimmen.</p>
  //   `
  // }
]);
</script>

<style scoped lang="scss">
.faq-container {
  max-width: 800px;
  margin: 0 auto;
}

:deep(ul), :deep(ol) {
  list-style-position: outside;
  // padding-left: 0.5rem;
  margin-left: 1rem;
  margin-bottom: 1rem;
}

</style>